import { BroadcastFormData } from '../types/broadcast.types';
import { Check, Calendar, Send, Image, Video, Music, Type, Link, MousePointerClick, Shuffle, Sparkles, ChevronDown } from 'lucide-react';
import { Switch } from '@/components/ui/switch';
import { SearchableSelect } from '@/components/ui/searchable-select';
import { LANGUAGES } from '../../Connection/constants/config-options';
import { useQuery } from '@tanstack/react-query';
import { getAiKeys } from '@/services/api/ai-key';
import { Loader2, AlertCircle } from 'lucide-react';
import { AUDIENCE_WINDOW_OPTIONS, AUDIENCE_NEEDS_TEMPLATE, type AudienceWindow } from '@/constants/audienceWindow';

interface StepReviewProps {
    formData: BroadcastFormData;
    onChange?: (aiTranslate: boolean, targetLanguage: string, aiProvider: string, sourceLanguage?: string) => void;
    onIncludeInactiveChange?: (value: boolean) => void;
    onAudienceWindowChange?: (value: AudienceWindow) => void;
}

const blockIcons: Record<string, typeof Type> = {
    text: Type,
    image: Image,
    video: Video,
    audio: Music,
};

const blockLabels: Record<string, string> = {
    text: 'Texto',
    image: 'Foto',
    video: 'Vídeo',
    audio: 'Áudio',
};

const PROVIDER_MAP = [
    { value: 'openai', label: 'OpenAI' },
    { value: 'claude', label: 'Claude' },
    { value: 'gemini', label: 'Gemini' },
    { value: 'grok', label: 'Grok' },
    { value: 'deepseek', label: 'DeepSeek' },
];

export default function StepReview({ formData, onChange, onIncludeInactiveChange, onAudienceWindowChange }: StepReviewProps) {
    const { data: aiKeys = [], isLoading: isLoadingKeys } = useQuery({
        queryKey: ['ai-keys'],
        queryFn: getAiKeys,
        staleTime: 1000 * 60 * 5,
    });

    const activeProviders = aiKeys
        .filter(key => key.is_active)
        .map(key => key.provider.toLowerCase());

    const availableOptions = PROVIDER_MAP.filter(p => activeProviders.includes(p.value));
    const hasProviders = availableOptions.length > 0;

    // Se o provedor selecionado não está mais disponível, avisa ou reseta
    const currentProviderValid = formData.aiProvider ? activeProviders.includes(formData.aiProvider.toLowerCase()) : false;

    return (
        <div className="space-y-6">
            <div className="text-center mb-6">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Check className="h-8 w-8 text-green-600" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900">Revisão Final</h3>
                <p className="text-sm text-gray-600 mt-2">
                    Confira todos os detalhes antes de enviar
                </p>
            </div>

            {/* ── AI Translation Global Config ── */}
            <div className="flex justify-center">
                <div className="inline-flex items-center gap-4 px-4 py-2 bg-white border border-gray-200 rounded-full shadow-sm">
                    <div className="flex items-center gap-2">
                        <div
                            className="flex items-center gap-2"
                            title={!hasProviders && !isLoadingKeys ? "Nenhuma chave de I.A configurada ou ativa." : undefined}
                        >
                            <Switch
                                id="global-ai-translate"
                                checked={!!formData.aiTranslate && hasProviders}
                                disabled={!hasProviders || isLoadingKeys}
                                onCheckedChange={(checked) => {
                                    if (checked && !formData.aiProvider && availableOptions.length > 0) {
                                        onChange?.(checked, formData.targetLanguage || 'Inglês - Estados Unidos', availableOptions[0].value, formData.sourceLanguage);
                                    } else {
                                        onChange?.(checked, formData.targetLanguage || 'Inglês - Estados Unidos', formData.aiProvider || 'openai', formData.sourceLanguage);
                                    }
                                }}
                                className="data-[state=checked]:bg-[#192335]"
                            />
                            <div className="flex items-center gap-1.5 ml-1">
                                <Sparkles className={`w-4 h-4 ${formData.aiTranslate && hasProviders ? 'text-blue-500 fill-blue-50' : 'text-gray-400'}`} />
                                <label
                                    htmlFor="global-ai-translate"
                                    className={`text-sm font-medium cursor-pointer ${!hasProviders ? 'text-gray-400' : 'text-gray-700'}`}
                                >
                                    Traduzir com I.A
                                </label>
                                {!hasProviders && !isLoadingKeys && (
                                    <AlertCircle className="w-3.5 h-3.5 text-amber-500" />
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="w-px h-8 bg-gray-100" />

                    {/* Source Language */}
                    <div className="flex items-center gap-1.5">
                        <span className="text-xs text-gray-400 whitespace-nowrap">De:</span>
                        <input
                            type="text"
                            value={formData.sourceLanguage || ''}
                            onChange={(e) => onChange?.(!!formData.aiTranslate, formData.targetLanguage || 'Inglês - Estados Unidos', formData.aiProvider || 'openai', e.target.value || undefined)}
                            placeholder="Auto"
                            className="w-20 bg-transparent pl-1 py-1 text-sm font-medium text-gray-900 focus:outline-none placeholder:text-gray-400"
                        />
                    </div>

                    <div className="w-px h-8 bg-gray-100" />

                    {/* Target Language */}
                    <SearchableSelect
                        value={formData.targetLanguage || 'Inglês - Estados Unidos'}
                        onValueChange={(v) => onChange?.(!!formData.aiTranslate, v, formData.aiProvider || 'openai', formData.sourceLanguage)}
                        options={LANGUAGES}
                        searchPlaceholder="Buscar idioma..."
                        className="border-0 shadow-none bg-transparent hover:bg-gray-100 h-8 text-sm font-medium w-auto min-w-[140px] rounded-lg"
                    />

                    <div className="w-px h-8 bg-gray-100" />

                    <div className="relative group min-w-[100px]">
                        {isLoadingKeys ? (
                            <div className="flex items-center justify-center px-4">
                                <Loader2 className="w-4 h-4 animate-spin text-gray-400" />
                            </div>
                        ) : (
                            <>
                                <select
                                    value={currentProviderValid ? formData.aiProvider : (availableOptions[0]?.value || '')}
                                    disabled={!hasProviders}
                                    onChange={(e) => onChange?.(!!formData.aiTranslate, formData.targetLanguage || 'Inglês - Estados Unidos', e.target.value, formData.sourceLanguage)}
                                    className={`appearance-none bg-transparent pl-2 pr-8 py-1 text-sm font-medium focus:outline-none cursor-pointer ${!hasProviders ? 'text-gray-400' : 'text-gray-900'}`}
                                >
                                    {availableOptions.map(opt => (
                                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                                    ))}
                                    {!hasProviders && <option value="">Nenhum</option>}
                                </select>
                                <ChevronDown className="absolute right-1 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                            </>
                        )}
                    </div>
                </div>
            </div>

            {/* ── Quem recebe: filtro de janela (mesmo da esteira) ── */}
            <div className="flex flex-col items-center gap-1">
                <div className="inline-flex items-center gap-3 px-4 py-2 bg-white border border-gray-200 rounded-full shadow-sm">
                    <label htmlFor="audience-window" className="text-sm font-medium text-gray-700">
                        🎯 Quem recebe
                    </label>
                    <select
                        id="audience-window"
                        value={formData.audienceWindow ?? 'all'}
                        onChange={(e) => onAudienceWindowChange?.(e.target.value as AudienceWindow)}
                        className="text-sm rounded-md border border-gray-200 bg-white px-2 py-1 focus:outline-none focus:ring-2 focus:ring-[#7dd3fc]/40"
                    >
                        {AUDIENCE_WINDOW_OPTIONS.map((o) => (
                            <option key={o.v} value={o.v}>{o.label}</option>
                        ))}
                    </select>
                </div>
                <p className="text-[11px] text-gray-500">
                    {AUDIENCE_WINDOW_OPTIONS.find((o) => o.v === (formData.audienceWindow ?? 'all'))?.hint}
                </p>
                {AUDIENCE_NEEDS_TEMPLATE.includes(formData.audienceWindow ?? 'all') && !formData.customTemplateId && (
                    <p className="text-[11px] text-amber-600">
                        Leads fora da janela 24h (e fora dos 7 dias do anúncio) recusam mensagem sem template aprovado (<code>#10</code>).
                    </p>
                )}
            </div>

            {/* ── Force send: incluir leads inativos (avançado) ── */}
            <div className="flex justify-center">
                <div
                    className="inline-flex items-center gap-3 px-4 py-2 bg-white border border-gray-200 rounded-full shadow-sm"
                    title="Por padrão broadcasts pulam leads que receberam erro letal do Meta. Marque para forçar envio também a esses (recovery)."
                >
                    <Switch
                        id="include-inactive"
                        checked={!!formData.includeInactive}
                        onCheckedChange={(checked) => onIncludeInactiveChange?.(!!checked)}
                        className="data-[state=checked]:bg-amber-600"
                    />
                    <label
                        htmlFor="include-inactive"
                        className="text-sm font-medium text-gray-700 cursor-pointer"
                    >
                        Forçar envio para leads inativos
                    </label>
                    {formData.includeInactive && (
                        <span className="text-xs text-amber-700 bg-amber-50 rounded px-2 py-0.5">
                            inclui leads com erro
                        </span>
                    )}
                </div>
            </div>

            <div className="max-w-2xl mx-auto space-y-4 max-h-[500px] overflow-y-auto pr-2">
                {/* Páginas Selecionadas */}
                {formData.selectedPages.length > 0 && (
                    <div className="bg-white border-2 border-gray-200 rounded-lg p-4">
                        <p className="text-xs text-gray-500 font-medium mb-3">
                            Páginas Selecionadas ({formData.selectedPages.length})
                        </p>
                        <div className="space-y-2">
                            {formData.selectedPages.map((page) => (
                                <div key={page.page_id} className="flex items-center gap-3">
                                    <div className="w-8 h-8 bg-[#0f172a] rounded-full flex items-center justify-center flex-shrink-0">
                                        <span className="text-white font-semibold text-xs">
                                            {page.page_name.substring(0, 2).toUpperCase()}
                                        </span>
                                    </div>
                                    <p className="text-sm font-medium text-gray-900">{page.page_name}</p>
                                </div>
                            ))}
                        </div>
                        {formData.selectedPages.length > 1 && (
                            <p className="text-xs text-amber-700 bg-amber-50 rounded-md px-3 py-2 mt-3">
                                Será criado 1 broadcast por página ({formData.selectedPages.length} broadcasts no total)
                            </p>
                        )}
                    </div>
                )}

                {/* Blocos de Conteúdo */}
                {formData.blocks.length > 0 && (
                    <div className="bg-white border-2 border-gray-200 rounded-lg p-4">
                        <p className="text-xs text-gray-500 font-medium mb-3">
                            Conteúdo ({formData.blocks.length} bloco{formData.blocks.length > 1 ? 's' : ''})
                        </p>
                        <div className="space-y-2">
                            {formData.blocks.map((block, index) => {
                                const Icon = blockIcons[block.type] || Type;
                                const label = blockLabels[block.type] || block.type;
                                const buttonCount = block.buttons?.length || 0;

                                return (
                                    <div key={index} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                                        <div className="flex-shrink-0 mt-0.5">
                                            <Icon className="w-4 h-4 text-gray-500" />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <p className="text-xs text-gray-400 font-medium">Bloco {index + 1}: {label}</p>
                                            {block.type === 'text' && block.message && (
                                                <p className="text-sm text-gray-900 mt-1 line-clamp-2">{block.message}</p>
                                            )}
                                            {block.type === 'image' && block.media_url && (
                                                <img src={block.media_url} alt="" className="w-24 h-16 object-cover rounded mt-1" />
                                            )}
                                            {(block.type === 'video' || block.type === 'audio') && block.media_filename && (
                                                <p className="text-sm text-gray-600 mt-1">{block.media_filename}</p>
                                            )}
                                            {buttonCount > 0 && (
                                                <div className="flex items-center gap-1 flex-wrap mt-1.5">
                                                    {block.buttons!.map((btn, btnIdx) => (
                                                        <span key={btnIdx} className="inline-flex items-center gap-1 text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full">
                                                            {btn.type === 'url' ? <Link className="w-3 h-3" /> : <MousePointerClick className="w-3 h-3" />}
                                                            {btn.text || 'Botão'}
                                                            {btn.type === 'url' && btn.url_entries && btn.url_entries.length > 0 && (
                                                                <span className="flex items-center gap-0.5 text-[10px] bg-blue-200 text-blue-800 px-1 rounded-full font-bold">
                                                                    <Shuffle className="w-2 h-2" />
                                                                    {btn.url_entries.length} URL{btn.url_entries.length > 1 ? 's' : ''}
                                                                </span>
                                                            )}
                                                        </span>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}

                {/* Agendamento */}
                <div className="bg-white border-2 border-gray-200 rounded-lg p-4">
                    <p className="text-xs text-gray-500 font-medium mb-2">Agendamento</p>
                    {formData.isScheduled && formData.scheduledTime ? (
                        <div className="flex items-center gap-2">
                            <Calendar className="h-4 w-4 text-cyan-600" />
                            <div>
                                <p className="text-sm font-semibold text-gray-900">Agendado</p>
                                <p className="text-xs text-gray-600">
                                    {new Date(formData.scheduledTime).toLocaleString('pt-BR', {
                                        dateStyle: 'full',
                                        timeStyle: 'short'
                                    })}
                                </p>
                            </div>
                        </div>
                    ) : (
                        <div className="flex items-center gap-2">
                            <Send className="h-4 w-4 text-green-600" />
                            <p className="text-sm font-semibold text-gray-900">Envio Imediato</p>
                        </div>
                    )}
                </div>

                {/* Aviso Final */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <p className="text-sm text-blue-900">
                        <strong>Atenção:</strong> Após confirmar, {formData.selectedPages.length > 1
                            ? `${formData.selectedPages.length} broadcasts serão ${formData.isScheduled ? 'agendados' : 'enviados imediatamente'}, um para cada página selecionada.`
                            : `${formData.isScheduled ? 'seu broadcast será agendado' : 'seu broadcast será enviado imediatamente'} para todos os contatos da página selecionada.`
                        } Cada lead receberá {formData.blocks.length} mensage{formData.blocks.length > 1 ? 'ns' : 'm'} em sequência.
                    </p>
                </div>
            </div>
        </div>
    );
}
